import { Shield, Phone, FileText, Ship, CheckCircle, Star, Award, Crown, Sparkles } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * Individual Verification Badges - Unique design for each type
 */

// Phone Verified Badge - Teal/Cyan theme
export function PhoneVerifiedBadge({ 
  verified = false, 
  size = 'default',
  showLabel = false,
  className = ''
}) {
  if (!verified) return null;
  
  const sizeClasses = {
    small: 'h-5 w-5 text-[10px]',
    default: 'h-6 w-6 text-xs',
    large: 'h-8 w-8 text-sm'
  };
  
  const iconSizes = {
    small: 'h-2.5 w-2.5',
    default: 'h-3 w-3',
    large: 'h-4 w-4'
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span 
            className={cn(
              "inline-flex items-center gap-1",
              className
            )}
            data-testid="phone-verified-badge"
          >
            <span className={cn(
              "relative inline-flex items-center justify-center rounded-full",
              "bg-gradient-to-br from-teal-400 to-cyan-500",
              "shadow-sm shadow-teal-200",
              sizeClasses[size]
            )}>
              <Phone className={cn("text-white", iconSizes[size])} strokeWidth={2.5} />
            </span>
            {showLabel && (
              <span className="text-xs font-medium text-teal-700">Phone Verified</span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-teal-50 border-teal-200">
          <div className="flex items-center gap-2 text-teal-800">
            <Phone className="h-4 w-4" />
            <span className="font-medium">Phone Number Verified</span>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// GST Verified Badge - Emerald/Green theme with document icon
export function GSTVerifiedBadge({ 
  verified = false, 
  size = 'default',
  showLabel = false,
  className = ''
}) {
  if (!verified) return null;
  
  const sizeClasses = {
    small: 'h-5 w-5 text-[10px]',
    default: 'h-6 w-6 text-xs',
    large: 'h-8 w-8 text-sm'
  };
  
  const iconSizes = {
    small: 'h-2.5 w-2.5',
    default: 'h-3 w-3',
    large: 'h-4 w-4'
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span 
            className={cn(
              "inline-flex items-center gap-1",
              className
            )}
            data-testid="gst-verified-badge"
          >
            <span className={cn(
              "relative inline-flex items-center justify-center rounded-md",
              "bg-gradient-to-br from-emerald-400 to-green-600",
              "shadow-sm shadow-emerald-200",
              sizeClasses[size]
            )}>
              <FileText className={cn("text-white", iconSizes[size])} strokeWidth={2.5} />
            </span>
            {showLabel && (
              <span className="text-xs font-medium text-emerald-700">GST Verified</span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-emerald-50 border-emerald-200">
          <div className="flex items-center gap-2 text-emerald-800">
            <FileText className="h-4 w-4" />
            <span className="font-medium">GSTIN Verified</span>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// IEC Verified Badge - Amber/Orange theme with ship/export icon
export function IECVerifiedBadge({ 
  status = 'not_submitted', // 'not_submitted', 'pending', 'approved', 'rejected'
  size = 'default',
  showLabel = false,
  className = ''
}) {
  if (status !== 'approved') return null;
  
  const sizeClasses = {
    small: 'h-5 w-5 text-[10px]',
    default: 'h-6 w-6 text-xs',
    large: 'h-8 w-8 text-sm'
  };
  
  const iconSizes = {
    small: 'h-2.5 w-2.5',
    default: 'h-3 w-3',
    large: 'h-4 w-4'
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span 
            className={cn(
              "inline-flex items-center gap-1",
              className
            )}
            data-testid="iec-verified-badge"
          >
            <span className={cn(
              "relative inline-flex items-center justify-center",
              "rounded-lg rotate-45",
              "bg-gradient-to-br from-amber-400 to-orange-500",
              "shadow-sm shadow-amber-200",
              sizeClasses[size]
            )}>
              <Ship className={cn("text-white -rotate-45", iconSizes[size])} strokeWidth={2.5} />
            </span>
            {showLabel && (
              <span className="text-xs font-medium text-amber-700">IEC Approved</span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-amber-50 border-amber-200">
          <div className="flex items-center gap-2 text-amber-800">
            <Ship className="h-4 w-4" />
            <span className="font-medium">IEC Code Approved (Export License)</span>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Fully Verified Seller Badge - Premium Gold/Purple gradient with crown
export function FullyVerifiedBadge({ 
  phoneVerified = false, 
  gstinVerified = false, 
  iecStatus = 'not_submitted',
  size = 'default',
  showLabel = true,
  variant = 'default', // 'default', 'compact', 'prominent'
  className = ''
}) {
  const isFullyVerified = phoneVerified && gstinVerified && iecStatus === 'approved';
  
  if (!isFullyVerified) return null;
  
  const sizeClasses = {
    small: 'h-5 px-2 text-[10px]',
    default: 'h-6 px-2.5 text-xs',
    large: 'h-8 px-3 text-sm'
  };
  
  const iconSizes = {
    small: 'h-3 w-3',
    default: 'h-3.5 w-3.5',
    large: 'h-4 w-4'
  };

  if (variant === 'compact') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span 
              className={cn(
                "inline-flex items-center justify-center",
                "rounded-full",
                "bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500",
                "shadow-lg shadow-purple-200/50",
                "animate-pulse",
                size === 'small' ? 'h-5 w-5' : size === 'large' ? 'h-8 w-8' : 'h-6 w-6',
                className
              )}
              data-testid="fully-verified-badge-compact"
            >
              <Crown className={cn("text-white", iconSizes[size])} strokeWidth={2} />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="bg-gradient-to-r from-violet-50 to-purple-50 border-purple-200">
            <VerificationTooltipContent />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span 
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full",
              "bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500",
              "text-white font-semibold",
              "shadow-lg shadow-purple-300/40",
              "border border-purple-300/30",
              sizeClasses[size],
              variant === 'prominent' && "ring-2 ring-purple-200 ring-offset-1",
              className
            )}
            data-testid="fully-verified-badge"
          >
            <Crown className={iconSizes[size]} strokeWidth={2} />
            {showLabel && <span>Verified Seller</span>}
            <Sparkles className={cn(iconSizes[size], "opacity-80")} strokeWidth={2} />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-gradient-to-r from-violet-50 to-purple-50 border-purple-200 p-3">
          <VerificationTooltipContent />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Tooltip content for fully verified badge
function VerificationTooltipContent() {
  return (
    <div className="text-sm">
      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-purple-200">
        <div className="p-1.5 rounded-full bg-gradient-to-r from-violet-500 to-purple-500">
          <Crown className="h-4 w-4 text-white" />
        </div>
        <div>
          <p className="font-bold text-purple-800">Trusted Verified Seller</p>
          <p className="text-[10px] text-purple-600">All credentials verified</p>
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <PhoneVerifiedBadge verified={true} size="small" />
          <span className="text-xs text-slate-600">Phone Verified</span>
        </div>
        <div className="flex items-center gap-2">
          <GSTVerifiedBadge verified={true} size="small" />
          <span className="text-xs text-slate-600">GST Registered</span>
        </div>
        <div className="flex items-center gap-2">
          <IECVerifiedBadge status="approved" size="small" />
          <span className="text-xs text-slate-600">Export Licensed</span>
        </div>
      </div>
    </div>
  );
}

// Combined Verification Status Display
export function VerificationStatus({ 
  phoneVerified = false, 
  gstinVerified = false, 
  iecStatus = 'not_submitted',
  size = 'default',
  layout = 'horizontal', // 'horizontal', 'vertical', 'compact'
  showLabels = false,
  className = ''
}) {
  const isFullyVerified = phoneVerified && gstinVerified && iecStatus === 'approved';
  const hasAnyVerification = phoneVerified || gstinVerified || iecStatus === 'approved';
  
  if (!hasAnyVerification) return null;

  // If fully verified, show the premium badge
  if (isFullyVerified && layout !== 'vertical') {
    return <FullyVerifiedBadge 
      phoneVerified={phoneVerified}
      gstinVerified={gstinVerified}
      iecStatus={iecStatus}
      size={size}
      className={className}
    />;
  }

  // Show individual badges
  return (
    <div 
      className={cn(
        "inline-flex gap-1.5",
        layout === 'vertical' && "flex-col",
        className
      )}
      data-testid="verification-status"
    >
      {isFullyVerified && (
        <FullyVerifiedBadge 
          phoneVerified={phoneVerified}
          gstinVerified={gstinVerified}
          iecStatus={iecStatus}
          size={size}
          variant="compact"
        />
      )}
      <PhoneVerifiedBadge verified={phoneVerified} size={size} showLabel={showLabels} />
      <GSTVerifiedBadge verified={gstinVerified} size={size} showLabel={showLabels} />
      <IECVerifiedBadge status={iecStatus} size={size} showLabel={showLabels} />
    </div>
  );
}

// Partial Verification Badge - for sellers with 1-2 verifications
export function PartialVerificationBadge({ 
  phoneVerified = false, 
  gstinVerified = false, 
  iecStatus = 'not_submitted',
  size = 'default',
  className = ''
}) {
  const verifications = [
    phoneVerified,
    gstinVerified,
    iecStatus === 'approved'
  ].filter(Boolean).length;
  
  if (verifications === 0 || verifications === 3) return null;
  
  const sizeClasses = {
    small: 'h-5 px-1.5 text-[10px]',
    default: 'h-6 px-2 text-xs',
    large: 'h-7 px-2.5 text-sm'
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span 
            className={cn(
              "inline-flex items-center gap-1 rounded-full",
              "bg-slate-100 text-slate-600 font-medium",
              "border border-slate-200",
              sizeClasses[size],
              className
            )}
            data-testid="partial-verification-badge"
          >
            <Shield className="h-3 w-3" />
            <span>{verifications}/3 Verified</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">
          <div className="text-sm space-y-1">
            <p className="font-medium mb-2">Verification Status</p>
            <div className="flex items-center gap-2">
              {phoneVerified ? (
                <CheckCircle className="h-3.5 w-3.5 text-green-500" />
              ) : (
                <div className="h-3.5 w-3.5 rounded-full border-2 border-slate-300" />
              )}
              <span className={phoneVerified ? 'text-green-700' : 'text-slate-400'}>Phone</span>
            </div>
            <div className="flex items-center gap-2">
              {gstinVerified ? (
                <CheckCircle className="h-3.5 w-3.5 text-green-500" />
              ) : (
                <div className="h-3.5 w-3.5 rounded-full border-2 border-slate-300" />
              )}
              <span className={gstinVerified ? 'text-green-700' : 'text-slate-400'}>GSTIN</span>
            </div>
            <div className="flex items-center gap-2">
              {iecStatus === 'approved' ? (
                <CheckCircle className="h-3.5 w-3.5 text-green-500" />
              ) : (
                <div className="h-3.5 w-3.5 rounded-full border-2 border-slate-300" />
              )}
              <span className={iecStatus === 'approved' ? 'text-green-700' : 'text-slate-400'}>IEC</span>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default VerificationStatus;
